import React, { createContext, useContext, useState, useCallback } from 'react';
import { fabric } from 'fabric';

interface EditorState {
  canvas: fabric.Canvas | null;
  layers: fabric.Object[];
  activeLayer: fabric.Object | null;
  selectedTool: string;
  zoom: number;
  background: string | null;
  selectedObject: fabric.Object | null;
  setSelectedObject: (object: fabric.Object | null) => void;
  setCanvas: (canvas: fabric.Canvas) => void;
  addToHistory: () => void;
  setZoom: (zoom: number) => void;
  setBackground: (background: string) => void;
}

export const EditorContext = createContext<EditorState | null>(null);

export const useEditor = () => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditor must be used within an EditorProvider');
  }
  return context;
};

export const EditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null);
  const [layers, setLayers] = useState<fabric.Object[]>([]);
  const [activeLayer, _setActiveLayer] = useState<fabric.Object | null>(null);
  const [selectedTool, _setSelectedTool] = useState('select');
  const [zoom, setZoom] = useState(1);
  const [background, setBackground] = useState<string | null>(null);
  const [selectedObject, setSelectedObject] = useState<fabric.Object | null>(null);

  const addToHistory = useCallback(() => {
    if (!canvas) return;
    setLayers([...canvas.getObjects()]);
  }, [canvas]);

  const value = {
    canvas,
    layers,
    activeLayer,
    selectedTool,
    zoom,
    background,
    selectedObject,
    setSelectedObject,
    setCanvas,
    addToHistory,
    setZoom,
    setBackground,
  };

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}; 